// The SOL value of a listed LST from its pool's own on-chain state (docs/19-staking-plan.md section 3.4 item 3):
// pure readers over account bytes, so the app reads the accounts over its own RPC and nothing else is asked. Layouts
// and pool addresses: docs/evidence/staking/2026-10-05/jupiter-quotes/lst-stake-pool-rates-derived.json (SPL stake
// pool: pool_mint at 162, total_lamports at 258, pool_token_supply at 266) and
// stake-pools/decoded-stake-pools-marinade-infinity.json (Marinade State, Anchor; Infinity PoolStateV2). The Infinity
// value is an estimate: its layout was read by us, not checked against Sanctum's IDL (docs/19 sections 2 and 3.4).

import { address } from '@solana/kit';
import { addressBytes } from './vault.js';

export type LstPoolKind = 'spl-stake-pool' | 'marinade' | 'infinity';

export interface LstPool {
  symbol: string;
  mint: string;
  kind: LstPoolKind;
  /** The account holding the rate: the stake pool, the Marinade State or the Infinity pool state PDA. */
  pool: string;
  /** The owner `pool` must have. */
  owner: string;
  /** True when the reading is an estimate (Infinity: layout read by us, value from the pool's own sum). */
  estimate: boolean;
}

/** One entry per LST in `LST_MINTS` (jupiter.ts). */
export const LST_POOLS: readonly LstPool[] = [
  { symbol: 'JitoSOL', mint: 'J1toso1uCk3RLmjorhTtrVwY9HJ7X8V9yYac6Y7kGCPn', kind: 'spl-stake-pool', pool: 'Jito4APyf642JPZPx3hGc6WWJ8zPKtRbRs4P815Awbb', owner: 'SPoo1Ku8WFXoNDMHPsrGSTSG1Y47rzgn41SLUNakuHy', estimate: false },
  { symbol: 'JupSOL', mint: 'jupSoLaHXQiZZTSfEWMTRRgpnyFm8f6sZdosWBjx93v', kind: 'spl-stake-pool', pool: '8VpRhuxa7sUUepdY3kQiTmX9rS5vx4WgaXiAnXq4KCtr', owner: 'SPMBzsVUuoHA4Jm6KunbsotaahvVikZs1JyTW6iJvbn', estimate: false },
  { symbol: 'mSOL', mint: 'mSoLzYCxHdYgdzU16g5QSh3i5K3z3KZK7ytfqcJm7So', kind: 'marinade', pool: '8szGkuLTAux9XMgZ2vtY39jVSowEcpBfFfD8hXSEqdGC', owner: 'MarBmsSgKXdrN1egZf5sqe1TMai9K1rChYNDJgjq7aD', estimate: false },
  { symbol: 'INF', mint: '5oVNBeEEQvYi1cX3ir8Dx5n1P7pdxydbGF2X4TxVusJm', kind: 'infinity', pool: 'AYhux5gJzCoeoc1PoJ1VxwPDe22RwcvpHviLDD1oCGvW', owner: '5ocnV1qiCgaQR8Jb8xWnVbApfaygJ8tNoZfgPwsgx9kx', estimate: true },
];

export function lstPoolOf(mint: string): LstPool | undefined {
  return LST_POOLS.find((p) => p.mint === mint);
}

/** SOL per token as a fraction: `lamports = amount * num / den`. */
export interface LstRate {
  num: bigint;
  den: bigint;
}

export class LstReadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LstReadError';
  }
}

const u64 = (d: Uint8Array, at: number): bigint => {
  if (d.length < at + 8) throw new LstReadError(`account too short for a u64 at ${at} (${d.length} bytes)`);
  return new DataView(d.buffer, d.byteOffset, d.byteLength).getBigUint64(at, true);
};
const sameKey = (d: Uint8Array, at: number, key: string): boolean => {
  const k = addressBytes(address(key));
  return d.length >= at + 32 && k.every((b, i) => d[at + i] === b);
};

/** SPL stake pool: `total_lamports / pool_token_supply`, after checking the account type (1) and the pool mint. */
export function splStakePoolRate(data: Uint8Array, mint: string): LstRate {
  if (data[0] !== 1) throw new LstReadError('not an SPL stake pool account (account type is not 1)');
  if (!sameKey(data, 162, mint)) throw new LstReadError('the stake pool names a different pool mint');
  const den = u64(data, 266);
  if (den === 0n) throw new LstReadError('the stake pool has no tokens in circulation');
  return { num: u64(data, 258), den };
}

/** Marinade State (Anchor, 8-byte discriminator): `msol_price / 2^32`, after checking the mSOL mint at offset 8. */
export function marinadeRate(data: Uint8Array, mint: string): LstRate {
  if (!sameKey(data, 8, mint)) throw new LstReadError('the Marinade state names a different mSOL mint');
  const num = u64(data, 512);
  if (num === 0n) throw new LstReadError('the Marinade state has no mSOL price');
  return { num, den: 1n << 32n };
}

/** Infinity PoolStateV2 `total_sol_value` (offset 0) over the INF mint's supply (offset 36 of the mint account): an estimate. */
export function infinityRate(poolState: Uint8Array, mintAccount: Uint8Array, mint: string): LstRate {
  if (!sameKey(poolState, 144, mint)) throw new LstReadError('the Infinity pool state names a different LP mint');
  const den = u64(mintAccount, 36);
  if (den === 0n) throw new LstReadError('the INF mint has no supply');
  return { num: u64(poolState, 0), den };
}

/** The accounts a rate needs, in order: the pool account, then (Infinity) the mint. */
export function lstRateAccounts(p: LstPool): string[] {
  return p.kind === 'infinity' ? [p.pool, p.mint] : [p.pool];
}

/** The rate from the accounts `lstRateAccounts` names (same order), checking the pool account's owner. */
export function lstRate(p: LstPool, accounts: ({ data: Uint8Array; owner: Uint8Array } | null)[]): LstRate {
  const [pool, mintAccount] = accounts;
  if (!pool) throw new LstReadError(`the ${p.symbol} pool account was not found`);
  const owner = addressBytes(address(p.owner));
  if (!owner.every((b, i) => pool.owner[i] === b)) throw new LstReadError(`the ${p.symbol} pool account has an unexpected owner`);
  if (p.kind === 'spl-stake-pool') return splStakePoolRate(pool.data, p.mint);
  if (p.kind === 'marinade') return marinadeRate(pool.data, p.mint);
  if (!mintAccount) throw new LstReadError('the INF mint account was not found');
  return infinityRate(pool.data, mintAccount.data, p.mint);
}

/** Lamports worth `amount` base units at `rate` (rounded down). */
export function lstSolValue(amount: bigint, rate: LstRate): bigint {
  return (amount * rate.num) / rate.den;
}
